import { useSearchParams } from 'react-router-dom';
import { Typography, Tabs, Alert, Card } from 'antd';
import { TagOutlined } from '@ant-design/icons';
import MarketIndicatorMapper from '../MarketIndicatorMapper';
import MarketLabelsTab from '../MarketLabelsTab';
import MarketIndicatorMethodologyMapper from '../MarketIndicatorMethodologyMapper';
import FscLabelTab from '../FscLabelTab';

const { Title, Text } = Typography;

const FSC_LABEL_TAB_KEY = 'fsc-label';
const DEFAULT_TAB_KEY = 'market-indicator';
const TAB_KEYS = ['methodology-market-indicator', DEFAULT_TAB_KEY, 'market-labels', FSC_LABEL_TAB_KEY];

function MarketIndicatorMitigationMapper() {
  // The tab lives in the URL (?tab=fsc-label) so a tab can be bookmarked or shared.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const activeTab = requestedTab && TAB_KEYS.includes(requestedTab) ? requestedTab : DEFAULT_TAB_KEY;
  const isFscTab = activeTab === FSC_LABEL_TAB_KEY;

  const setActiveTab = (key: string) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', key);
        return next;
      },
      // Switching tabs is not a navigation, so Back should leave the page, not step through tabs.
      { replace: true },
    );

  return (
    <div style={{ padding: 24, background: '#f5f5f5', minHeight: '100vh', boxSizing: 'border-box' }}>
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ flex: '1 1 auto' }}>
          <Title level={3} style={{ marginBottom: 0 }}>
            <TagOutlined style={{ marginRight: 8 }} />
            Market labels Mapper
          </Title>
          <Text type="secondary">
            Map registry methodologies to market labels.
          </Text>
        </div>
        {/* FSC labels are the exception to the Saturday sync: they hit Supply Bridge at once. */}
        <Alert
          key={isFscTab ? 'fsc' : 'sync'}
          type={isFscTab ? 'warning' : 'info'}
          showIcon
          closable
          message={
            isFscTab
              ? "FSC labels take effect immediately: tagging a project changes its Apple Spec status in Supply Bridge right away. The label is internal only and isn't shown to customers."
              : "Please note that changes saved here won't appear on the platform immediately. They will be applied during the next automatic registry sync, which runs every Saturday."
          }
          style={{ width: '50%', flexShrink: 0 }}
        />
      </div>
      <Card
        bordered={false}
        style={{ borderRadius: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          type="card"
          items={[
            {
              key: 'methodology-market-indicator',
              label: 'Harmonized Methodology Pre-Mapping',
              children: <MarketIndicatorMethodologyMapper />,
            },
            {
              key: 'market-indicator',
              label: 'Project Id Level mapping',
              children: <MarketIndicatorMapper />,
            },
            {
              key: 'market-labels',
              label: 'Market labels Metadata',
              children: <MarketLabelsTab />,
            },
            {
              key: FSC_LABEL_TAB_KEY,
              label: 'FSC label',
              children: <FscLabelTab />,
            },
          ]}
        />
      </Card>
    </div>
  );
}

export default MarketIndicatorMitigationMapper;
